import { Suspense, lazy } from "react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { convex } from "../utils/convex";
import { AuthProvider, useAuthContext } from "../contexts/AuthContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Layout from "./Layout";
import { ToasterProvider } from "./ui/toaster";
import ErrorBoundary from "./ErrorBoundary";
import { ConvexErrorHandler } from "./ConvexErrorHandler";

const SignInPage = lazy(() => import("./pages/SignInPage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const DomainsPage = lazy(() => import("./pages/DomainsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const CLIAuthPage = lazy(() => import("./pages/CLIAuthPage"));
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((module) => ({ default: module.OnboardingPage }))
);

function RouteLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

function HomeRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  
  // Wait for Clerk to load before redirecting
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isSignedIn ? <Navigate to="/dashboard" replace /> : <Navigate to="/sign-in" replace />;
}

function PrivateRoute({ children, requireOnboarding = true }: { children: React.ReactElement; requireOnboarding?: boolean }) {
  const { isSignedIn, isLoaded } = useAuth();
  const { hasCompletedOnboarding, isLoading: authLoading } = useAuthContext();
  const location = useLocation();
  
  // Show loading spinner while Clerk is initializing
  if (!isLoaded || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Redirect to sign-in if not authenticated
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }
  
  // Redirect to onboarding if not completed (unless on onboarding page or explicitly disabled)
  if (requireOnboarding && !hasCompletedOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
}

export default function App() {
  const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || "pk_test_placeholder";
  
  if (clerkPubKey === "pk_test_placeholder") {
    console.warn("⚠️ Using placeholder Clerk key. Make sure VITE_CLERK_PUBLISHABLE_KEY is set in .env.local");
  } else {
    console.log("✅ Clerk key loaded successfully:", clerkPubKey.substring(0, 20) + "...");
  }
  
  return (
    <ErrorBoundary>
      <ClerkProvider publishableKey={clerkPubKey}>
        <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
          <AuthProvider>
            <ToasterProvider>
              <ConvexErrorHandler>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <Routes>
                    <Route element={<Layout />}>
                      <Route index element={<HomeRedirect />} />
                      <Route path="sign-in" element={<SignInPage />} />
                      <Route path="sign-up" element={<SignUpPage />} />
                      <Route path="cli-auth" element={<CLIAuthPage />} />
                      <Route
                        path="onboarding"
                        element={
                          <PrivateRoute requireOnboarding={false}>
                            <OnboardingPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="dashboard"
                        element={
                          <PrivateRoute>
                            <DashboardPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="domains"
                        element={
                          <PrivateRoute>
                            <DomainsPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <PrivateRoute>
                            <SettingsPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="stats"
                        element={
                          <PrivateRoute>
                            <StatsPage />
                          </PrivateRoute>
                        }
                      />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                  </Routes>
                </Suspense>
              </ConvexErrorHandler>
            </ToasterProvider>
          </AuthProvider>
        </ConvexProviderWithClerk>
      </ClerkProvider>
    </ErrorBoundary>
  );
}
